/**
 * Component for a task module in the home page.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright (c) Ircam Amplify 2025-2026
 */

import { getJobReportFromSession, requestJobProcessing } from '@/api'
import { SortDirection } from '@/components/IampTable'
import { UploadState } from '@/components/jobui/FileUploader'
import {
  AudioFileCheck,
  AudioSpec,
  isFileUploaded,
  isRequirementMet,
  UploadFileInfo,
} from '@/components/jobui/UploadFileInfo'
import {
  ModuleConfig,
  MultiInputMode,
} from '@/components/ModuleConfig/ModuleConfig'
import {
  mimeToFileFormat,
  ModuleMimeType,
} from '@/components/ModuleConfig/ModuleDef'
import { ModuleInputConfig } from '@/components/ModuleConfig/ModuleInputConfig'
import '@/components/styles/Input.css'

import { logger, LogTag } from '@/config/logger'
import { getInputConfig } from '@/pages/TaskCreation/TaskCreationPage'
import {
  RootState,
  setJobListSortDirection,
  useDispatch,
  useSelector,
} from '@/store' // Adjust the path based on your project structure
import { isDebugMockup } from '@/utils/TestUtil'
import { FileUtil, TextUtil } from '@amplify3/duplo-back-end'
import { FormControl, Radio, RadioGroup } from '@mui/material'
import FormControlLabel from '@mui/material/FormControlLabel'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { BiCoin } from 'react-icons/bi'
import { useNavigate } from 'react-router-dom'
import ModuleContent from './HomeModuleContent'
import * as Def from './HomeModuleDef'
import { HomeModuleErrorProps } from './HomeModuleError'
import { HomeResultProps } from './HomeResult'
import { ProcessingResult } from './ProcessingResult'
import './styles/homemodule.css'

enum DebugOption {
  None = 'normal',
  TestProcessingComplete = 'Test processing complete',
  TestInProgress = 'Test In Progress',
  TestCreditError = 'Test credit error',
  TestBackEndError = 'Test backend error',
}

interface ModuleCostProps {
  costText: string
}

interface ModuleInfoProps extends ModuleCostProps {
  title: string
  description: string
  iconSrc: string
  iconAlt: string
}

interface HomeModuleProps extends ModuleInfoProps, HomeResultProps {
  id?: string
  fileSpec?: string
  handleProcessingResult: (
    response: any,
    jobId: string,
    fileName?: string
  ) => void
  moduleConfig: ModuleConfig
  moduleParamComp?: React.ElementType
  moduleParamProps?: any
  updateModuleParamList?: (newParam: Record<string, any>) => void

  moduleParamList?: Record<string, any>

  processingResult: ProcessingResult | undefined
  createProcessingResult: () => ProcessingResult
  setProcessingResult: any
  cleanSavedData: () => void
  getSavedIasUrl: () => string | undefined
  getSavedFileName: () => string | undefined

  restartButtonMsg?: string
}

const HomeModule = (props: HomeModuleProps) => {
  const MAX_FILE_COUNT_PER_INPUT = 1
  const POLLING_INTERVAL = 2000 // 2 seconds
  const BACKEND_ERROR_INSUFFICIENT_CREDIT = 402

  /**
   * The structure storing the input of the module, like files,
   * custom parameters.
   */
  const [inputData, setInputData] = useState<Record<string, UploadFileInfo>>({})
  const [currentFile, setCurrentFile] = useState<UploadFileInfo | undefined>(
    undefined
  )
  const [moduleError, setModuleError] = useState<HomeModuleErrorProps | null>(
    null
  )

  /**
   * The status of the module.
   */
  const [moduleStatus, setModuleStatus] = useState<Def.ModuleState>(
    Def.ModuleState.ReadyToUpload
  )

  const [debugError, setDebugError] = useState<DebugOption>(DebugOption.None)
  const [processJobId, setProcessJobId] = useState<string | undefined>(
    undefined
  )

  const [resetStatus, setResetStatus] = useState<number | undefined>(undefined)

  const inputConfig = props.moduleConfig.inputList[0]
  const inputFileSpec = getFileSpecMessage(inputConfig)
  const inputId = inputConfig.name
  const dispatch = useDispatch()

  // Audio context management for Audio file processing
  const audioContextRef = useRef<AudioContext | null>(null)
  if (!audioContextRef.current) {
    // Create once when the component mounts
    audioContextRef.current = new AudioContext()
  }

  const userState = useSelector((state: RootState) => state.user)
  const navigate = useNavigate() // Get the navigate function

  function getFileSpecMessage(inputConfig: ModuleInputConfig) {
    return inputConfig.tip.replace(/, max \d+ tracks/, '')
  }

  useEffect(() => {
    if (Object.keys(userState).length === 0 || !userState.userInfo) {
      // User logged out → Clear cookies
      logger.debug(LogTag.HomePage, 'User signed out, clearing cookies...')
      props.cleanSavedData()
    }
  }, [userState]) // Runs when authentication state changes

  /**
   * Get data stored in the cookies at the first rendering of the component.
   */
  useEffect(() => {
    logger.debug(
      LogTag.HomePage,
      'First rendering: load processing data from cookies'
    )
    const savedResult = props.createProcessingResult()
    savedResult.load()

    if (userState?.userInfo?.value?.user_email !== savedResult.userId) {
      logger.debug(
        LogTag.HomePage,
        `Changed user: clean saved data: saved user=${savedResult.userId}, session user=${userState?.userInfo?.value?.user_email}`
      )
      props.cleanSavedData()
    } else if (savedResult.jobId) {
      props.setProcessingResult(savedResult)
      if (savedResult.isComplete()) {
        // A previous job has been saved completely
        logger.debug(
          LogTag.HomePage,
          'First rendering: previous job completed.'
        )
        setModuleStatus(Def.ModuleState.Complete)
      } else {
        // A previous job in progress has been saved.
        logger.debug(
          LogTag.HomePage,
          'First rendering: previous job in progress.'
        )
        setProcessJobId(savedResult.getSavedJobId())
      }
    }
  }, [])

  useEffect(() => {
    logger.debug(LogTag.HomePage, 'inputData:', inputData)
  }, [inputData])

  useEffect(() => {
    logger.debug(LogTag.HomePage, `moduleStatus: ${moduleStatus}`)
    if (Def.isError(moduleStatus)) {
      props.cleanSavedData()
    }
  }, [moduleStatus])

  useEffect(() => {
    logger.debug(LogTag.HomePage, `processJobId: ${processJobId}`)
  }, [processJobId])

  /**
   * Get the name of an input given a string in the format <name>_<index>
   * or <name>.
   *
   * @param name The string to parsed.
   * @returns The name without index.
   */
  const getInputName = (name: string): string => {
    const lastUnderscoreIndex = name.lastIndexOf('_')
    if (lastUnderscoreIndex === -1) {
      // No underscore, return as is
      return name
    }

    const afterUnderscore = name.substring(lastUnderscoreIndex + 1)
    if (/^\d+$/.test(afterUnderscore)) {
      // Check if only numbers
      return name.substring(0, lastUnderscoreIndex)
    }

    return name // Return unchanged if not only numbers
  }

  /**
   * Check if the given file fulfills the requirements of the module.
   *
   * @param fileInfo The information about the file to upload.
   * @param inputName The name of the input of the module
   *
   * @returns AudioFileCheck or undefined.
   */
  const checkFileRequirements = async (
    fileInfo: UploadFileInfo,
    inputName: string
  ): Promise<AudioFileCheck | undefined> => {
    if (
      !props.moduleConfig ||
      !fileInfo.localFile ||
      !audioContextRef.current
    ) {
      return undefined
    }

    // Search for the input configuration
    let inputConfig = undefined
    const inputConfigName = getInputName(inputName)
    for (const currentConfig of props.moduleConfig.inputList) {
      if (currentConfig.name === inputConfigName) {
        inputConfig = currentConfig
      }
    }

    if (!inputConfig) {
      // Not found
      return undefined
    }

    // Check audio file
    const checking = new AudioFileCheck(inputConfig)

    if (inputConfig.mimeTypeList) {
      // Convert list of ModuleMimeType to a list of strings
      const mimeStringList: string[] = [...inputConfig.mimeTypeList]

      checking.isTypeValid = mimeStringList.includes(
        fileInfo.mimetype as ModuleMimeType
      )
    }

    // Check audio specifications in case audio files
    if (fileInfo.mimetype.startsWith('audio/')) {
      const audioSpec = new AudioSpec()

      if (fileInfo.remoteFile?.fileinfo) {
        // Got the audio specifications extracted by the backend
        // in a request response
        audioSpec.parseBackEndResponse(fileInfo.remoteFile?.fileinfo)
      } else {
        // Try to extract the audio specifications
        // with the browser's native decoder
        try {
          await audioSpec.decodeAudioFile(
            audioContextRef.current,
            fileInfo.localFile
          )
        } catch (decodeError) {
          // Can't decode the file: postpone the checking
          // until the file is uploaded and use the metadata retrieved
          // by the Storage module.
          logger.error(
            'Failed to decode audio data with Web AudioContext:',
            decodeError
          )
          checking.isTypeValid = undefined
        }
      }

      if (audioSpec) {
        checking.isTypeValid = true

        // Check minimum duration
        if (audioSpec.durationInSec && inputConfig.minDuration) {
          checking.isMinDurationValid =
            audioSpec.durationInSec >= inputConfig.minDuration
        }

        // Check maximum duration
        if (audioSpec.durationInSec && inputConfig.maxDuration) {
          checking.isMaxDurationValid =
            audioSpec.durationInSec <= inputConfig.maxDuration
        }

        // Check number of channels
        if (audioSpec.channelCount && inputConfig.channelCountList) {
          checking.isChannelCountValid = inputConfig.channelCountList.includes(
            audioSpec.channelCount
          )
        }
      }
    }

    return checking
  }

  /**
   * Increment an ID with the format <name>_<index>, e.g. "audioFile_1".
   *
   * @param currentId The ID to increment.
   * @returns The new ID or undefined if the ID has not the expected format.
   */
  function incrementId(currentId: string): string | undefined {
    // Split the ID into base and index parts
    const parts = currentId.split('_')

    // Ensure there are two parts
    if (parts.length == 2) {
      const baseId = parts[0]
      const index = parseInt(parts[1])

      if (isNaN(index)) {
        throw new Error('Index part of ID is not a number.')
      }

      // Increment the index
      const newIndex = index + 1

      // Construct the new ID
      return `${baseId}_${newIndex}`
    } else {
      return undefined
    }
  }

  /**
   * Start a new processing by resetting members and states.
   */
  function startNewProcessing(forceStatus?: Def.ModuleState | undefined) {
    logger.debug(LogTag.HomePage, 'Start a new processing')
    setModuleStatus(forceStatus ?? Def.ModuleState.ReadyToUpload)
    setInputData({})
    setCurrentFile(undefined)
    props.setProcessingResult(undefined)
    props.cleanSavedData()
    setProcessJobId(undefined)
    setModuleError(null)
    setResetStatus((prev) => (prev === undefined ? 1 : prev + 1))
  }

  /**
   * Check if the files of the given list fulfill the requirements of the
   * module.
   *
   * @param inputId The ID of the input.
   * @param fileInfoList The list of files.
   * @param currentInputData The current status of inputData.
   * @returns The updated inputData to set, with requirements status.
   */
  const checkFileList = async (
    inputId: string,

    fileInfoList: UploadFileInfo[],
    currentInputData: Record<string, UploadFileInfo>
  ) => {
    // Add input files
    let currentId: string | undefined = inputId

    const promises = fileInfoList.map(async (file) => {
      if (currentId) {
        if (
          !file.requirementCheck ||
          file.requirementCheck.isValid() === undefined
        ) {
          try {
            logger.debug(LogTag.HomePage, `Checking ${file.fileName}`)
            const checking = await checkFileRequirements(file, inputId)
            if (checking) {
              logger.debug(LogTag.HomePage, 'Checking:', checking)
              file.requirementCheck = checking
            }
          } catch (error) {
            logger.error('Error while checking file requirements:', error)
            handleBackendError('Error while checking file requirements.')
            setModuleStatus(Def.ModuleState.Error)
          }
        } else {
          logger.debug(LogTag.HomePage, `${file.fileName} already checked.`)
        }

        setCurrentFile(file)
        if (!file.isUploaded) {
          props.setProcessingResult(undefined)
        }

        const currentData = currentInputData[currentId]
        const isUploaded =
          file.isUploaded ||
          (FileUtil.areFilesEqual(file.localFile, currentData?.localFile) &&
            currentData?.isUploaded)
        const remoteFile = file.remoteFile ?? currentData?.remoteFile

        const fileCount = Object.keys(currentInputData).length
        if (
          (currentData && fileCount <= MAX_FILE_COUNT_PER_INPUT) ||
          (!currentData && fileCount < MAX_FILE_COUNT_PER_INPUT)
        ) {
          // Update fields concerning the file, and preserving isUploaded status
          currentInputData[currentId] = {
            ...currentData, // Existing data
            ...file, // New or updated data
            isUploaded,
            remoteFile,
          }
        }

        // Next ID
        currentId = incrementId(currentId)
      }
    })

    await Promise.all(promises)
    return currentInputData
  }

  /**
   * Add files to the list of inputs.
   *
   * @param inputId The ID of the input.
   * @param fileInfoList The list of files.
   *
   * If more than one file is in fileInfoList, they are set to the inputs
   * following designated by inputId.
   */
  const addInputFile = async (
    inputId: string,
    fileInfoList: UploadFileInfo[] | undefined
  ) => {
    logger.debug(LogTag.HomePage, 'addInputFile:', fileInfoList)
    if (fileInfoList) {
      const isNewProcessing = Def.isProcessingCompleted(moduleStatus)
      if (isNewProcessing) {
        // New file: restart a processing
        startNewProcessing(Def.ModuleState.AnalyzingFile)
      } else if (Def.isReadyToUpload(moduleStatus)) {
        setModuleStatus(Def.ModuleState.AnalyzingFile)
      }

      // Use functional state update to let React handle concurrent calls
      // and  process files.
      setInputData((prevState) => {
        const currentInputData = isNewProcessing ? {} : { ...prevState }
        // Process files asynchronously
        checkFileList(inputId, fileInfoList, currentInputData).then(
          (checkedInputData) => {
            // Merge the processed data into the latest state
            // after async operation completes
            setInputData((latestState) => ({
              ...latestState,
              ...checkedInputData,
            }))
          }
        )
        // Return the current state as is for now
        return currentInputData
      })
    } else {
      // Handle the case where no fileInfoList is provided
      setInputData((prevState: any) => {
        const updatedState = { ...prevState }
        delete updatedState[inputId]
        logger.debug(LogTag.HomePage, `Removed ${inputId} file.`)
        return updatedState
      })
    }
  }

  /**
   * Set the error message component to display error about insufficient funds
   * and credits.
   */
  function setCreditErrorComp() {
    setModuleError({
      title: Def.ModuleErrorTitle.InsufficientCredit,
      message: Def.ModuleErrorMessage.Upgrade,
      buttonText: Def.ModuleErrorButtonText.Upgrade,
      handleBack: () => navigate('/tierselection'),
    })
  }

  /**
   * Set the error message component to display a standard error message
   * from the backend.
   *
   * @param message The error message.
   *
   * The function is memoized handleBackendError using useCallback,
   * i.e. its result is cached, so the function will not be re-created
   * on every render. This avoids the following error:
   *
   * "Maximum update depth exceeded.
   * "This can happen when a component calls setState inside useEffect,
   * "but useEffect either doesn't have a dependency array,
   * "or one of the dependencies changes on every render. "
   */
  const handleBackendError = useCallback((message: string | ReactNode) => {
    setModuleError({
      detail: message,
      handleBack: () => startNewProcessing(undefined),
    })
  }, [])

  const handleFileUploaderChange = useCallback(
    (uploadStatus: UploadState, msg: (string | JSX.Element)[]) => {
      if (UploadState.Error === uploadStatus) {
        const strMsg =
          msg.length > 0 ? msg.join(' ') : 'Unexpected server error'
        logger.error(msg)
        handleBackendError(strMsg)
        setModuleStatus(Def.ModuleState.Error)
      }
    },
    [handleBackendError]
  ) // Include only the function dependency

  const handleRequirementError = (fileInfo: UploadFileInfo) => {
    if (fileInfo.requirementCheck) {
      let errorMessage = undefined
      const fileCheck = fileInfo.requirementCheck
      const requirements = fileCheck.requirements

      if (false === fileCheck.isTypeValid) {
        const uniqueFormats = new Set(
          requirements.mimeTypeList?.map((mime) => mimeToFileFormat[mime])
        )
        const formatCount = uniqueFormats.size
        const supportedFileFormat = [...uniqueFormats].join(', ') || ''
        errorMessage = `Only ${supportedFileFormat.toUpperCase()}
          ${TextUtil.is_or_are(formatCount)}
          supported.`
      } else if (
        requirements.minDuration &&
        false === fileCheck.isMinDurationValid
      ) {
        errorMessage = `Audio duration is too short.
          Minimum duration is
          ${TextUtil.formatDuration(requirements.minDuration)}.`
      } else if (
        requirements.maxDuration &&
        false === fileCheck.isMaxDurationValid
      ) {
        errorMessage = `Audio duration is too long.
          Maximum duration is
          ${TextUtil.formatDuration(requirements.maxDuration)}.`
      } else if (
        requirements.channelModeList &&
        false === fileCheck.isChannelCountValid
      ) {
        const supportedChannelList =
          requirements.channelModeList.join(', ') || ''
        errorMessage = `Only ${supportedChannelList}
          ${TextUtil.is_or_are(requirements.channelModeList.length)} supported.`
      }

      setModuleError({
        title: Def.ModuleErrorTitle.FileRequirement,
        buttonText: Def.ModuleErrorButtonText.Back,
        message: errorMessage,
        handleBack: () => startNewProcessing(undefined),
      })
    }

    setModuleStatus(Def.ModuleState.RequirementError)
  }

  /**
   * Generate the payloads to send to the back-end.
   *
   * @param inputData The data of the inputs.
   * @returns A list of payloads.
   */
  const getPayloadList = (
    inputData: any
  ): Record<string, any>[] | undefined => {
    const payloadList: Record<string, any>[] = []

    // Configuration parameters
    const paramSet: Record<string, any> = {}
    if (props.moduleParamList) {
      for (const [paramName, paramValue] of Object.entries(
        props.moduleParamList
      )) {
        paramSet[paramName] = TextUtil.normalizeValue(paramValue)
      }
    }

    // Get the list of valid input files
    const validFileList = Object.entries(inputData).reduce(
      (acc, [key, inputFile]) => {
        const info = inputFile as UploadFileInfo
        if (isFileUploaded(info) && isRequirementMet(info)) {
          acc[key] = info
        }
        return acc
      },
      {} as Record<string, UploadFileInfo>
    )

    let payload: Record<string, any> = { ...paramSet }

    for (const [, [key, fileInfo]] of Object.entries(validFileList).entries()) {
      const [inputId, inputConfig] = getInputConfig(key, props.moduleConfig)
      if (inputConfig) {
        // Found the input
        if (inputConfig['multiple']) {
          // Accept several input files in one job
          if (!payload[inputId]) {
            payload[inputId] = []
          }
          payload[inputId].push(fileInfo.remoteFile?.ias)
        } else {
          // One input file per job
          if (
            props.moduleConfig.multiplicity == MultiInputMode.OneJobPerInputFile
          ) {
            if (payload[inputId]) {
              payloadList.push(payload)
              // Prepare new payload for the next file
              payload = { ...paramSet }
            }
          }

          payload[inputId] = fileInfo.remoteFile?.ias
        }
      }
    }

    payloadList.push(payload)
    return payloadList
  }

  /**
   * Send a request of processing to backend and wait for the completion.
   */
  const requestProcessing = () => {
    const payloadList = getPayloadList(inputData)
    if (payloadList && payloadList.length > 0) {
      for (const [, payload] of (payloadList ?? []).entries()) {
        logger.debug(LogTag.HomePage, `Process file with payload: `, payload)

        requestJobProcessing(
          JSON.stringify(payload),
          props.moduleConfig?.info.endpoint,
          true // forceRefresh
        )
          .then((response) => {
            // Log the response when the task is done
            logger.debug(
              LogTag.HomePage,
              'Processing request response:',
              response
            )
            const jobId = response.id

            const startResult = props.createProcessingResult()
            startResult.jobId = jobId
            startResult.fileName = currentFile?.fileName
            startResult.iasUrl = currentFile?.remoteFile?.ias
            startResult.userId = userState?.userInfo?.value?.user_email
            startResult.save()
            setProcessJobId(jobId)
          })
          .catch((error) => {
            // Handle any error that might occur
            logger.error('Request processing error:', error)
            if (BACKEND_ERROR_INSUFFICIENT_CREDIT == error.response.status) {
              // Specific layout for the 402 error (insufficient credits)
              setCreditErrorComp()
              setModuleStatus(Def.ModuleState.CreditError)
            } else {
              handleBackendError(error.response.data.message)
              setModuleStatus(Def.ModuleState.Error)
            }
            setModuleStatus(Def.ModuleState.Error)
          })
      }
      dispatch(setJobListSortDirection(SortDirection.DESCENDING))
    }
  }

  /**
   * At each change of the job ID, request the results from the backend.
   */
  useEffect(() => {
    if (!processJobId || !processJobId.length) {
      return
    }

    setModuleStatus(Def.ModuleState.Processing)
    pollForResults(processJobId).catch((error) => {
      if (error instanceof Error) {
        const msg = error.message
        const lineList = msg.split('\n')
        const uid = crypto.randomUUID()

        handleBackendError(
          <div>
            {lineList.map((line) => (
              <p key={uid}>{line}</p>
            ))}
          </div>
        )
      } else {
        handleBackendError('Unknown error')
      }
      setModuleStatus(Def.ModuleState.Error)
    })
  }, [processJobId])

  /**
   * Poll results of the processing of the module from the backend.
   * @param jobId The job ID of the processing.
   * @returns The response containing the result.
   */
  async function pollForResults(jobId: string) {
    logger.debug(LogTag.HomePage, 'Start polling results for job ID', jobId)
    let attempts = 0
    while (true) {
      attempts++
      await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL))

      logger.debug(LogTag.HomePage, `Checking job status (attempt ${attempts})`)
      const response = await getJobReportFromSession(
        jobId,
        props.moduleConfig?.info.endpoint,
        true // forceRefresh
      )
      const status = response.job_infos.job_status
      logger.debug(
        LogTag.HomePage,
        `[pollForResults] jobId: ${jobId}, status: ${status}`
      )

      if (status === 'success') {
        logger.debug(LogTag.HomePage, 'Analysis complete.')
        logger.debug(LogTag.HomePage, 'Response: ', response)
        let result = undefined

        if (response.job_infos.report_info!.report.resultList) {
          // Get only the first result, as we send only one file to process
          result = response.job_infos.report_info!.report.resultList[0]
        } else {
          // Get the complete report
          result = response.job_infos.report_info!.report
        }
        logger.debug(LogTag.HomePage, 'Result: ', result)

        let fileName = currentFile?.fileName
        if (!fileName && result.ias && result.ias === props.getSavedIasUrl()) {
          fileName = props.getSavedFileName()
        }
        props.handleProcessingResult(response, jobId, fileName)

        return result
      } else if (status === 'error') {
        logger.error('Response error:', response)
        const reportErrorMsg =
          response?.job_infos?.report_info?.report?.errorMessage
        const msg =
          reportErrorMsg?.length > 0
            ? reportErrorMsg
            : response.statusText?.length > 0
              ? response.statusText
              : 'Unknown error'
        logger.error('Error message:', msg)
        throw new Error(msg)
      }
    }
  }

  /**
   * Update moduleStatus state depending on the user interaction and
   * the evolution of the processing.
   */
  useEffect(() => {
    logger.debug(LogTag.HomePage, 'currentFile:', currentFile)
    logger.debug(LogTag.HomePage, 'processingResult:', props.processingResult)

    if (DebugOption.None != debugError) {
      logger.debug(LogTag.HomePage, 'Test error mangement')
      return
    }

    // A file has been selected by the user
    switch (moduleStatus) {
      case Def.ModuleState.AnalyzingFile:
      case Def.ModuleState.Uploading:
        if (currentFile) {
          if (currentFile.requirementCheck) {
            if (currentFile.requirementCheck.isValid() == false) {
              // The file does not meet the requirements
              handleRequirementError(currentFile)
            } else {
              // The file meets the requirements
              if (currentFile.isUploaded) {
                // Already uploaded: request processing
                setModuleStatus(Def.ModuleState.PreparingProcess)
                requestProcessing()
              } else if (moduleStatus == Def.ModuleState.AnalyzingFile) {
                // The upload is currently uploading
                setModuleStatus(Def.ModuleState.Uploading)
              }
            }
          }
        }
        break

      case Def.ModuleState.Processing:
        if (props.processingResult?.isComplete()) {
          // The results are available, the processing is considered completed.
          setModuleStatus(Def.ModuleState.Complete)
          logger.debug(LogTag.HomePage, 'Status: complete.')
        }
        break
    }
  }, [currentFile, props.processingResult, moduleStatus, debugError])

  /**
   * Handle the selection of a debug test.
   *
   * @param event
   */
  const handleDebugOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    props.setProcessingResult(undefined)
    setDebugError(event.target.value as DebugOption)

    switch (event.target.value) {
      case DebugOption.TestProcessingComplete:
        setModuleStatus(Def.ModuleState.Complete)
        const debugResult = props.createProcessingResult()
        debugResult.setDebugData()
        props.setProcessingResult(debugResult)
        break

      case DebugOption.TestInProgress:
        setModuleStatus(Def.ModuleState.Processing)
        break
      case DebugOption.TestCreditError:
        setModuleStatus(Def.ModuleState.CreditError)
        setCreditErrorComp()
        break

      case DebugOption.TestBackEndError:
        setModuleStatus(Def.ModuleState.Error)
        handleBackendError(
          'Test an backend error message. Details about the error printed here.'
        )
        break

      case DebugOption.None:
        startNewProcessing()
        break
    }
  }

  /**
   * Customize debug components
   */
  const debugMuiTheme = createTheme({
    components: {
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontSize: '0.875rem', // 14px (text-sm)
          },
        },
      },
    },
  })

  const DebugSelector = () => {
    return (
      <ThemeProvider theme={debugMuiTheme}>
        <FormControl component="fieldset">
          <RadioGroup row value={debugError} onChange={handleDebugOptionChange}>
            <FormControlLabel
              value={DebugOption.None}
              control={<Radio />}
              label={DebugOption.None}
              sx={{ fontSize: '0.875rem' }}
            />
            <FormControlLabel
              value={DebugOption.TestInProgress}
              control={<Radio />}
              label={DebugOption.TestInProgress}
            />
            <FormControlLabel
              value={DebugOption.TestProcessingComplete}
              control={<Radio />}
              label={DebugOption.TestProcessingComplete}
            />
            <FormControlLabel
              value={DebugOption.TestCreditError}
              control={<Radio />}
              label={DebugOption.TestCreditError}
            />
            <FormControlLabel
              value={DebugOption.TestBackEndError}
              control={<Radio />}
              label={DebugOption.TestBackEndError}
            />
          </RadioGroup>
        </FormControl>
      </ThemeProvider>
    )
  }

  const ModuleCostComp = ({ costText }: ModuleCostProps) => {
    return (
      <div className="costInfo">
        <BiCoin className="icon" />
        <div className="costValue">{costText}</div>
      </div>
    )
  }

  const ModuleInfoComp = (props: ModuleInfoProps) => {
    return (
      <div className="moduleInfo">
        <img className="moduleIcon" src={props.iconSrc} alt={props.iconAlt} />
        <div className="moduleDescription">
          <div className="title">{props.title}</div>
          <div className="description">{props.description}</div>
        </div>
        <ModuleCostComp {...props} />
      </div>
    )
  }

  return (
    <div className="w-full" id={props.id}>
      {/* A switch to display a mock-up of detection results, for debug only */}
      {isDebugMockup() && <DebugSelector />}

      <div className="homeModule" data-process-job-id={processJobId}>
        <ModuleInfoComp {...props} />
        <ModuleContent
          {...props}
          fileSpec={props.fileName ?? inputFileSpec}
          inputConfig={inputConfig}
          inputId={inputId}
          inputData={inputData}
          moduleStatus={moduleStatus}
          setInputData={setInputData}
          addInputFile={addInputFile}
          handleFileUploaderChange={handleFileUploaderChange}
          fileName={props.processingResult?.fileName ?? currentFile?.fileName}
          error={moduleError}
          resetStatus={resetStatus}
          restartButtonMsg={props.restartButtonMsg}
          handleStartNewProcessing={() => {
            startNewProcessing()
          }}
        />
      </div>
    </div>
  )
}

export default HomeModule
